/**
 * @class
 * @description Gives event emitting/listening functionalities to its extenders.
 * @exports Emitter
 * @abstract
 */
export default class Emitter {
    constructor() {
        this._listeners = {};
    }
    /**
     * Sets a listener to a given event. Use {@link emit} to trigger those events.
     * @param {string} eventName Name of the event.
     * @param {callback} callback Callback of the event. Receives event and data.
     * @return {this} Polymorphic with the type of the child class.
     * @public
     */
    on(eventName, callback) {
        if (!this._listeners[eventName]) {
            this._listeners[eventName] = [];
        }
        this._listeners[eventName].push(callback);
        return this;
    }
    /**
     * Removes given callback from the listeners of this object.
     * @param {string} eventName Name of the event.
     * @param {callback} callback Callback of the event.
     * @return {this} Polymorphic with the type of the child class.
     * @public
     */
    off(eventName, callback) {
        if (this._listeners[eventName]) {
            const index = this._listeners[eventName].indexOf(callback);
            if (index !== -1) {
                this._listeners[eventName].splice(index, 1);
            }
        }
        return this;
    }
    /**
     * Emits given event, triggering all the associated callbacks.
     * @param {string} eventName Name of the event.
     * @param {object} data Custom data to be sent to the callbacks.
     * @return {this} Polymorphic with the type of the child class
     * @protected
     */
    emit(eventName, data = {}) {
        [eventName, '*'].forEach((event) => {
            if (this._listeners[event]) {
                this._listeners[event].forEach((callback) => {
                    callback({
                        type: eventName,
                        target: this,
                        data
                    });
                });
            }
        });
        return this;
    }
}
